import { message } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './index.less';
import { useTranslation } from 'react-i18next';

declare global {
    interface Window {
        zoom: number;
        fabric: any;
        canvas: any;
    }
}

interface IProps {
    close?: () => void;
    questionId: number;
}

interface SessionItem {
    data: string;
    questionId: number;
}

const Draft = (props: IProps) => {
    // 选择父节点插入，自动给父节点添加relative 属性便于定位覆盖
    const parentDom: any = document.querySelector('#sub-tiku__question-draft-content') || document.body;
    parentDom.style.position = 'relative';

    const { t } = useTranslation();

    const optionList = [
        { type: 'cancel', name: t('draft.undo'), class: 'sub-tiku__draft-cancel' },
        { type: 'advance', name: t('draft.redo'), class: 'sub-tiku__draft-advance' },
        { type: 'clear', name: t('draft.clear'), class: 'sub-tiku__draft-clear' },
        { type: 'close', name: t('draft.exit'), class: 'sub-tiku__draft-close' },
    ];
    const drawWidth = 2; // 笔触宽度
    let moveCount = 1; // 绘制移动计数器
    let doDrawing = false; // 绘制状态
    const color = '#FF2B00'; // 绘制颜色

    const [optionPosition, setPotionPosition] = useState({
        top: window.innerHeight / 2 - 150,
        // right: parentDom.offsetLeft,
    });

    const [redoHistory, setRedoHistory] = useState<string[]>([]);

    const mouseFrom = useRef({ x: 0, y: 0 });
    const mouseTo = useRef({ x: 0, y: 0 });
    const canvas = useRef<any>(null);

    const setSessionData = (type?: string) => {
        const data = canvas.current.toJSON();
        const sessionData = sessionStorage.getItem('TIKU-DraftData');
        const sessionArr: SessionItem[] = sessionData ? JSON.parse(sessionData) : [];
        const filterSessionArr: SessionItem[] = sessionArr.filter((item: any) => item.questionId !== props.questionId);
        sessionStorage.setItem(
            'TIKU-DraftData',
            JSON.stringify([
                ...filterSessionArr,
                {
                    data: type === 'clear' ? [] : data,
                    questionId: props.questionId,
                },
            ])
        );
    };

    // 坐标转换
    const transformMouse = (mouseX: number, mouseY: number) => {
        return { x: mouseX / window.zoom, y: mouseY / window.zoom };
    };

    // 解决上限问题
    const resolveLimitData = () => {
        const currentData = canvas.current.toJSON();

        if (currentData.objects.length < 20) {
            return;
        }

        const objects = canvas.current.getObjects();
        const tempCanvas = new window.fabric.Canvas('sub-tiku__question-draft-canvas', {
            isDrawingMode: true,
            defaultCursor: 'default',
        });

        for (let i = 0; i < 10; i++) {
            tempCanvas.add(objects[i]);
        }
        const dataURL = tempCanvas.toDataURL({
            format: 'png',
            quality: 0.8,
        });

        window.fabric.Image.fromURL(dataURL, (img: string) => {
            const prevData = canvas.current.toJSON();
            const leaveData = prevData.objects.slice(10);

            canvas.current.clear();
            canvas.current.add(img);

            const changeNowData = canvas.current.toJSON();
            changeNowData.objects = [...changeNowData.objects, ...leaveData];
            canvas.current.loadFromJSON(changeNowData);
            // .renderAll();
        });
    };

    const init = () => {
        // 初始化画板
        canvas.current = new window.fabric.Canvas('sub-tiku__question-draft-canvas', {
            isDrawingMode: true,
            defaultCursor: 'default',
        });

        canvas.current.freeDrawingCursor =
            'url(https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202303/01/982ec_20230301150928.png),default';

        canvas.current.freeDrawingBrush.color = color; // 设置自由绘颜色
        canvas.current.freeDrawingBrush.width = drawWidth;
        window.canvas = canvas.current;

        // 绑定画板事件
        canvas?.current.on('mouse:down', function (options: { e: { offsetX: number; offsetY: number } }) {
            const xy = transformMouse(options.e.offsetX, options.e.offsetY);
            mouseFrom.current.x = xy.x;
            mouseFrom.current.y = xy.y;
            doDrawing = true;
        });

        canvas?.current.on('mouse:up', function (options: { e: { offsetX: number; offsetY: number } }) {
            const xy = transformMouse(options.e.offsetX, options.e.offsetY);
            mouseTo.current.x = xy.x;
            mouseTo.current.y = xy.y;
            moveCount = 1;
            doDrawing = false;
        });

        canvas?.current.on('mouse:move', function (options: { e: { offsetX: number; offsetY: number } }) {
            if (moveCount % 2 && !doDrawing) {
                // 减少绘制频率
                return;
            }
            const xy = transformMouse(options.e.offsetX, options.e.offsetY);
            mouseTo.current.x = xy.x;
            mouseTo.current.y = xy.y;
        });

        canvas?.current.on('object:added', () => {
            setSessionData();
            resolveLimitData();
        });

        // 处理当前窗口这道题的储存数据
        const sessionData = sessionStorage.getItem('TIKU-DraftData');
        const sessionArr: SessionItem[] = sessionData ? JSON.parse(sessionData) : [];
        const currentSessionItme = sessionArr.find(item => item.questionId === props.questionId);
        if (currentSessionItme) {
            canvas.current.loadFromJSON(currentSessionItme.data)?.renderAll();
        }
    };

    const prevStepOperate = () => {
        if (canvas.current._objects.length > 0) {
            const currentData = canvas.current.toJSON();
            setRedoHistory([currentData.objects[currentData.objects.length - 1], ...redoHistory]);
            currentData.objects.pop();
            canvas.current.loadFromJSON(currentData).renderAll();
        } else {
            message.info(t('draft.noUndo'));
        }
    };

    const nextStepOperate = () => {
        if (redoHistory.length > 0) {
            const currentData = canvas.current.toJSON();
            currentData.objects = [...currentData.objects, redoHistory[0]];
            canvas.current.loadFromJSON(currentData).renderAll();
            setRedoHistory(redoHistory.filter((_, index) => index > 0));
        }
    };

    const onSelectTools = (type: string) => {
        switch (type) {
            case 'cancel':
                prevStepOperate();
                break;
            case 'advance':
                nextStepOperate();
                break;
            case 'clear':
                setRedoHistory([]);
                setSessionData('clear');
                canvas.current.clear();
                break;
            case 'close':
                props.close?.();
                break;
            default:
                message.info(t('draft.notImplemented'));
        }
    };

    // 设置缩放
    const setZoom = () => {
        canvas.current.setWidth(parentDom.offsetWidth);
        canvas.current.setHeight(parentDom.offsetHeight);
    };

    const optionResizePosition = () => {
        setZoom();
        setPotionPosition({
            top: window.innerHeight / 2 - 150,
            // right: parentDom.offsetLeft,
        });
    };

    useEffect(() => {
        init();
        window.addEventListener('resize', optionResizePosition);

        return () => {
            window.removeEventListener('resize', optionResizePosition);
        };
    }, []);

    return ReactDOM.createPortal(
        <div className="sub-tiku__draft-box">
            <div className="sub-tiku__draft-option-box" style={optionPosition}>
                {optionList.map(item => (
                    <div
                        className={classNames('sub-tiku__draft-option-item', item.class, {
                            draftDisabledCancel: false,
                            draftDisabledAdvance: redoHistory.length === 0,
                        })}
                        key={item.type}
                        onClick={() => onSelectTools(item.type)}
                    >
                        <div />
                        {item.name}
                    </div>
                ))}
            </div>
            <canvas height={parentDom.offsetHeight} id="sub-tiku__question-draft-canvas" width={parentDom.offsetWidth}>
                {t('draft.html5Tip')}
            </canvas>
        </div>,
        parentDom
    );
};

export default Draft;
